import React, { useState } from "react";
import { ArrowRight, Check, ChevronLeft } from "lucide-react";
import Button from "./ui/Button";
import {
  INDUSTRY_PRESETS,
  Industry,
  Density,
  IndustryPreset,
  FREE_LINK_CAP,
} from "../utils/industryPresets";

export interface IndustryPickerSelection {
  industry: Industry;
  density: Density;
  preset: IndustryPreset;
}

export interface IndustryPickerSkip {
  skipped: true;
}

export interface IndustryPickerRedirect {
  redirectTo: string;
  industry: Industry;
}

export type IndustryPickerResult =
  | IndustryPickerSelection
  | IndustryPickerSkip
  | IndustryPickerRedirect;

interface IndustryPickerProps {
  isPaidUser: boolean;
  /** Display name to address the user with on step 1. */
  displayName?: string;
  /** Called when the user picks (or skips). The caller persists `industry` and
   *  applies the preset to a new card if applicable. */
  onComplete: (result: IndustryPickerResult) => void;
}

const PERSONAS = INDUSTRY_PRESETS.filter((p) => p.kind === "persona");
const TEAMS = INDUSTRY_PRESETS.find((p) => p.kind === "team");

export const IndustryPicker: React.FC<IndustryPickerProps> = ({
  isPaidUser,
  displayName,
  onComplete,
}) => {
  const [step, setStep] = useState<"industry" | "density">("industry");
  const [picked, setPicked] = useState<IndustryPreset | null>(null);
  const [density, setDensity] = useState<Density>("curated");

  const handlePickIndustry = (preset: IndustryPreset) => {
    if (preset.kind === "team") {
      onComplete({
        redirectTo: preset.redirectTo || "/teams",
        industry: preset.industry,
      });
      return;
    }
    setPicked(preset);
    setStep("density");
  };

  const handleConfirmDensity = () => {
    if (!picked) return;
    onComplete({ industry: picked.industry, density, preset: picked });
  };

  // ─── Step 1 · Industry ───────────────────────────────────────────────────
  if (step === "industry") {
    return (
      <div className="min-h-screen bg-cream-50 dark:bg-coffee-900 py-16 px-6 sm:px-8">
        <div className="max-w-[1240px] mx-auto">
          <header className="mb-12 max-w-2xl">
            <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-4">
              Welcome{displayName ? `, ${displayName}` : ""}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-coffee-900 dark:text-cream-50 leading-[1.04] tracking-tight">
              What kind of work are you doing<span className="text-accent-500">?</span>
            </h1>
            <p className="mt-5 text-lg text-coffee-700 dark:text-cream-200">
              Pick the role closest to yours. We'll start your card with the links and the
              contact form that fit. You can change everything afterwards.
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PERSONAS.map((p) => (
              <button
                key={p.industry}
                type="button"
                onClick={() => handlePickIndustry(p)}
                className="text-left bg-cream-100 dark:bg-coffee-800 hover:bg-coffee-100 dark:hover:bg-coffee-700 transition-colors rounded-lg p-6 border border-latte-300 dark:border-latte-800 hover:border-coffee-700 dark:hover:border-cream-100"
              >
                <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-2">
                  {p.label}
                </p>
                <p className="font-display text-3xl text-coffee-900 dark:text-cream-50 leading-none">
                  {p.tileStat}<span className="text-accent-500">.</span>
                </p>
                <p className="mt-2 text-sm text-coffee-700 dark:text-cream-200">{p.tileStatCaption}</p>
                <p className="mt-3 text-xs text-coffee-600 dark:text-cream-200/80 leading-relaxed">
                  {p.tilePreview}
                </p>
              </button>
            ))}

            {/* Teams tile — distinct treatment, routes to /teams */}
            {TEAMS && (
              <button
                type="button"
                onClick={() => handlePickIndustry(TEAMS)}
                className="text-left bg-coffee-900 dark:bg-cream-50 text-cream-50 dark:text-coffee-900 hover:bg-coffee-800 dark:hover:bg-cream-100 transition-colors rounded-lg p-6 border border-coffee-900 dark:border-cream-50"
              >
                <p className="font-mono text-eyebrow uppercase opacity-70 mb-2">
                  {TEAMS.label}
                </p>
                <p className="font-display text-3xl leading-none">
                  {TEAMS.tileStat}<span className="text-accent-500">.</span>
                </p>
                <p className="mt-2 text-sm opacity-90">{TEAMS.tileStatCaption}</p>
                <p className="mt-3 text-xs opacity-70 leading-relaxed">{TEAMS.tilePreview}</p>
              </button>
            )}
          </div>

          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => onComplete({ skipped: true })}
              className="text-sm text-coffee-600 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50 underline underline-offset-4 decoration-latte-300 hover:decoration-coffee-900 dark:hover:decoration-cream-50"
            >
              Skip — I'll set my card up from scratch
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 2 · Density ────────────────────────────────────────────────────
  if (!picked || !picked.densities) {
    // Defensive — shouldn't happen since `team` kind redirects on step 1.
    onComplete({ skipped: true });
    return null;
  }

  const minimal = picked.densities.minimal;
  const curated = picked.densities.curated;
  const kitchenSink = picked.densities["kitchen-sink"];

  const options: { id: Density; count: number; label: string; sub: string; locked?: boolean; lockedReason?: string }[] = [
    { id: "minimal",      count: minimal.length,      label: "Minimal",     sub: "The fastest path to a useful card." },
    { id: "curated",      count: curated.length,      label: "Curated",     sub: "The five links your role uses most." },
    {
      id: "kitchen-sink", count: kitchenSink.length,  label: "Everything",  sub: "Every link your role might want.",
      locked: !isPaidUser && kitchenSink.length > FREE_LINK_CAP,
      lockedReason: `Free is capped at ${FREE_LINK_CAP} links — Pro unlocks the full set.`,
    },
  ];

  const activeLinks = density === "minimal" ? minimal : density === "curated" ? curated : kitchenSink;

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-coffee-900 py-12 px-6 sm:px-8">
      <div className="max-w-[1240px] mx-auto">
        <button
          type="button"
          onClick={() => { setPicked(null); setStep("industry"); }}
          className="inline-flex items-center gap-1 text-sm text-coffee-600 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50 mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> Back to roles
        </button>

        <header className="mb-10 max-w-2xl">
          <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-3">
            {picked.label} · density
          </p>
          <h1 className="font-display text-4xl sm:text-5xl text-coffee-900 dark:text-cream-50 leading-[1.04] tracking-tight">
            How busy do you want your card<span className="text-accent-500">?</span>
          </h1>
          <p className="mt-5 text-lg text-coffee-700 dark:text-cream-200">
            We'll start your card with these placeholder links — fill them in (or delete what
            you don't use) before you publish.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
          {/* Left — density picker tiles */}
          <div className="lg:col-span-5 space-y-3">
            {options.map((opt) => {
              const isActive = density === opt.id;
              const isLocked = !!opt.locked;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => { if (!isLocked) setDensity(opt.id); }}
                  disabled={isLocked}
                  className={`w-full text-left p-5 rounded-lg transition-colors border ${
                    isActive
                      ? "bg-coffee-900 text-cream-50 border-coffee-900"
                      : isLocked
                        ? "bg-cream-100 dark:bg-coffee-800 text-coffee-400 dark:text-latte-400 border-latte-300 dark:border-latte-800 cursor-not-allowed"
                        : "bg-cream-50 dark:bg-coffee-900 text-coffee-900 dark:text-cream-50 border-latte-300 dark:border-latte-800 hover:border-coffee-700 dark:hover:border-cream-100"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-display text-xl">{opt.label}<span className={isActive ? "text-accent-500" : "text-accent-500"}>.</span></p>
                    {isLocked ? (
                      <span className="font-mono text-[10px] uppercase tracking-eyebrow px-2 py-0.5 bg-coffee-900 text-cream-50 rounded">Pro</span>
                    ) : (
                      <span className={`font-mono text-xs ${isActive ? "opacity-80" : "text-coffee-600 dark:text-cream-200"}`}>
                        {opt.count} links
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${isActive ? "opacity-80" : "text-coffee-700 dark:text-cream-200"}`}>{opt.sub}</p>
                  {isLocked && opt.lockedReason && (
                    <p className="mt-2 text-xs text-coffee-500 dark:text-cream-200/60">{opt.lockedReason}</p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right — live preview list */}
          <div className="lg:col-span-7">
            <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-3">
              Preview · what we'll pre-load
            </p>
            <div className="bg-cream-100 dark:bg-coffee-800 border border-latte-300 dark:border-latte-800 rounded-lg p-5 space-y-2">
              {activeLinks.map((l, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded border text-sm ${
                    l.linkType === "contact_form"
                      ? "bg-coffee-900 dark:bg-cream-50 text-cream-50 dark:text-coffee-900 border-coffee-900 dark:border-cream-50 font-medium"
                      : "bg-cream-50 dark:bg-coffee-900 text-coffee-900 dark:text-cream-50 border-latte-300 dark:border-latte-800"
                  }`}
                >
                  <span className="flex-1">{l.title}</span>
                  {l.linkType === "contact_form" && (
                    <span className="font-mono text-[10px] uppercase tracking-eyebrow opacity-80">Form</span>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-coffee-600 dark:text-cream-200/80">
              {activeLinks.filter((l) => l.linkType === "contact_form").length > 0
                ? "Includes a Contact form link with persona-specific qualifying questions on Pro."
                : ""}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-latte-300 dark:border-latte-800">
          <button
            type="button"
            onClick={() => onComplete({ skipped: true })}
            className="text-sm text-coffee-600 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50 underline underline-offset-4 decoration-latte-300 hover:decoration-coffee-900 dark:hover:decoration-cream-50"
          >
            Skip — set up from scratch instead
          </button>
          <Button
            onClick={handleConfirmDensity}
            size="lg"
            trailingIcon={<ArrowRight className="w-4 h-4" />}
          >
            Start my card
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IndustryPicker;
